import React, { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3';
import { data } from './BarData'; // Import the data array

const color = {
  current: '#32D5EB',
  next: '#E684E2',
}

const BarChart = () => {
  const chartRef = useRef();
  const tooltipRef = useRef();
  const [currentProperty, setCurrentProperty] = useState('current');
  const [currentCategory, setCurrentCategory] = useState('diversity');

  const getDimensions = () => {
    const width = window.innerWidth > 768 ? 700 : window.innerWidth * 0.9;
    const height = window.innerWidth > 768 ? 800 : 1000;
    const fontSize = window.innerWidth > 768 ? "11px" : "12px";
    return { width, height, fontSize };
  };

  const [dimensions, setDimensions] = useState(getDimensions());

  useEffect(() => {
    const handleResize = () => {
      setDimensions(getDimensions());
      drawChart();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [currentCategory]);

  useEffect(() => {
    drawChart();
  }, [currentCategory, dimensions]);

  let sortedData;

  if (currentCategory === 'populism' || currentCategory === 'diversity') {
    sortedData = [...data].sort((a, b) => a['current_' + currentCategory] - b['current_' + currentCategory]);
  } else {
    sortedData = [...data].sort((a, b) => b['current_' + currentCategory] - a['current_' + currentCategory]);
  }

  const margin = { top: 0, right: 30, bottom: 0, left: 100 };
  
  const drawChart = () => {
  // Clear existing chart
    d3.select('#chart').selectAll('*').remove();
    drawSingleChart("current");
    drawSingleChart("next");
  };

  const handleCategoryChange = (event) => {
    setCurrentCategory(event.target.value);
  };

  const drawSingleChart = (property) => {
    const filteredData = sortedData.filter(d => {
      return (d['current_' + currentCategory] !== "" && d['current_' + currentCategory] !== undefined) || (d['next_' + currentCategory] !== "" && d['next_' + currentCategory] !== undefined);
    });

    const svg = d3.select('#chart')
      .attr('viewBox', `0 0 ${dimensions.width + margin.left + margin.right} ${dimensions.height + margin.top + margin.bottom}`)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Set up the scales
    const xScale = d3.scaleLinear()
      .domain([0, d3.max(filteredData, d => d['current_' + currentCategory])])
      .range([0, dimensions.width]);

    const yScale = d3.scaleBand()
      .domain(filteredData.map(d => d.country))
      .range([0, dimensions.height])
      .padding(0.6);

    var yAxis = d3.axisLeft()
      .scale(yScale)
      .tickSize(0)
      .tickFormat(function(d, i){return filteredData[i].country})

    var yText = svg.append("g")
      .attr("class", "y axis")
      .attr("transform", "translate(-8, 0 )")
      .call(yAxis)
      .style("font-size", dimensions.fontSize);

    const bars = svg.selectAll('rect')
      .data(filteredData)

    // Remove old bars
    bars.exit().remove();

    // Add the bars
    bars.enter()
      .append('rect')
      .attr('class', property)
      .attr('fill', color[property])
      .attr("transform", "translate(0, " + (property === 'current' ? -4 : 4) + ")")
      .attr('x', xScale(0))
      .attr('width', d => xScale(d[property + '_' + currentCategory]))
      .attr('y', d => yScale(d.country))
      .attr('height', yScale.bandwidth())
      .on('mouseover', handleMouseOver)
      .on('mousemove', handleMouseMove)
      .on('mouseout', handleMouseOut)
      // .style('opacity', 0)
      .merge(bars)
      .transition()
      .duration(750)
      // .style('opacity', 1)

    // Add labels to bars if needed
    // bars.enter()
    //   .append("text")
    //   .attr('class', property)
    //   .attr("transform", "translate(0, " + (property === 'current' ? -4 : 4) + ")")
    //   .attr("x", d => xScale(d[property + '_' + currentCategory]) + 5)
    //   .attr("y", d => yScale(d.country_cn) + yScale.bandwidth() / 2)
    //   .text(d => d[property + '_' + currentCategory])
    //   .style("font-size", dimensions.fontSize)
    //   .attr("dy", ".35em");
  };

  const getButtonLabel = (value) => {
    const button = buttonConfigs.find(button => button.value === value);
    return button ? button.label : '';
  };

  const getPropertyLabel = (value) => {
    const property = propertyConfigs.find(property => property.value === value);
    return property ? property.label : '';
  };

  const handleMouseOver = (event, d) => {
    const property = event.target.className.baseVal;
    console.log(property)
    d3.select(tooltipRef.current)
      .style('display', 'block')
      .html(`
      <h3>${d.country}</h3>
      ${property === 'current' ? `<span class="current">Pre-election Party：</span>${d.current_party_en}` : `<span class="next">Post-election Party：</span>${d.next_party_en}`}
      <span class="${property}">${getPropertyLabel(currentCategory)} ${getButtonLabel(property)}: </span>${d[property + '_' + currentCategory]}
      `);
  };

  const handleMouseMove = (event) => {
    d3.select(tooltipRef.current)
      .style('left', (event.pageX + 10) + 'px')
      .style('top', (event.pageY - 25) + 'px');
  };

  const handleMouseOut = () => {
    d3.select(tooltipRef.current)
      .style('display', 'none');
  };

  const buttonConfigs = [
    { "label": "Before Election", "value": "current" },
    { "label": "After Election", "value": "next" },
    { "label": "Comparison", "value": "compare" }
  ];

  const propertyConfigs = [
    { "label": "Anti-Diversity", "value": "diversity", "desc": "A composite score by V-Dem assessing party authoritarianism, disregard for minority rights, and tendency to demonize opposition or support political violence." },
    { "label": "Populism", "value": "populism", "desc": "A V-Dem score measuring a party’s reliance on anti-elite, anti-establishment, and populist rhetoric to mobilize support." },
    { "label": "Immigration", "value": "immigrant", "desc": "Higher scores indicate stronger support for immigration policies." },
    { "label": "LGBT Rights", "value": "lgbt", "desc": "Higher scores reflect greater support for LGBT rights and equality." },
    { "label": "Female Leader", "value": "female", "desc": "Represents the proportion of women in national leadership roles within the party." },
    { "label": "Economic Policy", "value": "economy", "desc": "Lower scores indicate left-leaning policies (more regulation, higher taxes, welfare); higher scores indicate right-leaning policies (privatization, deregulation, tax cuts)." }
  ];

  return (
    <>
      <div className="navigation bar">
        <div className="navigation-wrapper bar">
          {buttonConfigs.map((button, index) => (
            <button
              key={index}
              onClick={() => setCurrentProperty(button.value)}
              className={ currentProperty === button.value ? "selected" : ""}
            >
              {button.label}
            </button>
          ))}
          <select value={currentCategory} onChange={handleCategoryChange}>
            {propertyConfigs.map((property, index) => (
              <option key={index} value={property.value}>{property.label}</option>
            ))
            }
          </select>
          <div className='desc-wrapper'>
            <div className='desc'>
              {propertyConfigs.find(button => button.value === currentCategory).desc}
            </div>
          </div>
        </div>
      </div>
      <svg id="chart" className={"chart " + currentProperty} ref={chartRef}></svg>
      <div ref={tooltipRef} className="tooltip" style={{ position: 'absolute', display: 'none', pointerEvents: 'none', backgroundColor: 'white', border: '1px solid #ddd', padding: '10px 10px 15px', borderRadius: '3px', boxShadow: '0 1px 2px rgba(0, 0, 0, .1)', fontSize: '12px' }}></div>
    </>
  );
};

export default BarChart;
